import { useEffect, useRef } from 'react'
import { useAuth } from '@hooks/auth/useAuth'
import AuthRoutesConfig from '@route/index'
import Loader from '@components/shared/Loader/Loader'
import { analyticsService, getTelemetryConfig } from 'lib/insights'
import { Hub } from 'aws-amplify/utils'
import '@aws-amplify/ui-react/styles.css'
import '@styles/Authentication.css'
import './App.css'
import ErrorBoundary from '@components/shared/ErrorBoundary/ErrorBoundary'
import { LanguageProvider } from 'core/contexts/LanguageContext'

import { fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth'

// Import OAuth listener for multi-page applications
import 'aws-amplify/auth/enable-oauth-listener'
import { useToast } from '@hooks/ui/useToast'
import { AuthLayout } from 'layouts/AuthLayout/AuthLayout'
import { restoreOAuthQueryParams } from 'core/utils/oauth-params'
import { useNavigate } from 'react-router-dom'
import { PAGE_URL } from '@constants/path'
import {
  getDeviceIdFromUrl,
  setMixpanelDeviceId,
  removeMpIdFromUrl,
} from 'core/utils/mixpanel-device-id'
import { setAuthLoading } from 'redux/Loading/loadingSlice'
import { useDispatch } from 'react-redux'

export const App: React.FC = () => {
  const { isAuthReady, setSession } = useAuth()
  const { displayToast } = useToast()
  const navigate = useNavigate()
  const displayToastRef = useRef(displayToast)
  const dispatch = useDispatch()

  useEffect(() => {
    displayToastRef.current = displayToast
  }, [displayToast])

  // Read OAuth error from URL (e.g., error_description) and display it once
  useEffect(() => {
    try {
      const url = new URL(window.location.href)
      const hasOAuthParams =
        url.searchParams.has('code') ||
        url.searchParams.has('state') ||
        url.searchParams.has('error') ||
        url.searchParams.has('error_description')

      // Only restore on OAuth callback or /auth routes (never on /security)
      if (hasOAuthParams) {
        restoreOAuthQueryParams()
      }
      const error = url.searchParams.get('error')
      const errorDescription = url.searchParams.get('error_description')

      if (error || errorDescription) {
        // Decode description replacing '+' with spaces first
        const decodedDescription = errorDescription
          ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
          : null

        const message = decodedDescription || 'OAuth sign-in failed.'
        // Show longer toast for OAuth errors so users can read it fully
        displayToastRef.current?.(message, 'error', 6000)

        // Clean handled params from URL to avoid repeated toasts
        url.searchParams.delete('error')
        url.searchParams.delete('error_description')
        url.searchParams.delete('state')
        const newSearch = url.searchParams.toString()
        const newUrl =
          url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash
        window.history.replaceState({}, '', newUrl)
      }
    } catch {
      // no-op
    }
  }, [])

  useEffect(() => {
    // Capture device ID from main platform bridge before Mixpanel initialization
    const deviceIdFromUrl = getDeviceIdFromUrl()
    if (deviceIdFromUrl) {
      setMixpanelDeviceId(deviceIdFromUrl)
      analyticsService?.setDeviceIdBeforeInit(deviceIdFromUrl)
      // Remove mp_id from URL immediately after capture to keep URL clean
      removeMpIdFromUrl()
    }

    // Initialize telemetry services directly
    const telemetryConfig = getTelemetryConfig()

    if (telemetryConfig?.mixpanel?.enabled && analyticsService) {
      analyticsService?.init(telemetryConfig?.mixpanel).catch((error: any) => {
        console.error('Failed to initialize Analytics:', error)
      })
    }

    // Set up OAuth callback listener
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      console.log('[OAUTH][HUB]', payload.event, payload)
      switch (payload.event) {
        case 'signInWithRedirect':
          console.log('✅ OAuth sign-in successful')
          // Restore saved query params immediately after successful OAuth
          // This runs at the right time after OAuth callback is detected
          restoreOAuthQueryParams()
          // The AuthenticatedApp component will handle the redirect
          break
        case 'signInWithRedirect_failure': {
          const msg = (payload as any)?.message || 'OAuth sign-in failed.'
          console.error('❌ OAuth sign-in failed:', msg)
          try {
            displayToastRef.current?.(msg, 'error')
          } catch {}
          break
        }
        case 'customOAuthState':
          console.log('📝 Custom OAuth state received:', (payload as any).data)
          // Handle custom state if you passed any during sign-in
          break
        default:
          break
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])

  // After redirect, ensure tokens are present; if linking delayed token issuance, retry once
  useEffect(() => {
    const ensureSessionOrRetry = async () => {
      try {
        // Params already restored in first useEffect - no need to restore again

        // Give Cognito a brief moment to settle post-redirect
        await new Promise((r) => setTimeout(r, 250))

        const session = await fetchAuthSession({ forceRefresh: false })
        console.log('[OAUTH][CHECK] session', session)
        const hasIdToken = !!session?.tokens?.idToken?.toString()
        console.log('[OAUTH][CHECK] hasIdToken', hasIdToken)
        if (hasIdToken) {
          try {
            sessionStorage.removeItem('auth:retryAttempted')
            setSession(session)
            const redirectUrl = new URLSearchParams(window.location.search).get(
              'redirect'
            )
            const action = new URLSearchParams(window.location.search).get(
              'action'
            )
            // Do not navigate here on redirect; let RedirectValidationGate handle validation and routing
            console.log(
              '[OAUTH][REDIRECT] Detected redirect param (deferred to gate):',
              redirectUrl
            )
            if (action && action === 'delete_account') {
              navigate(PAGE_URL.AUTH_SECURITY_DELETE_ACCOUNT, { replace: true })
            }
            return
          } catch {
            console.log('[OAUTH][READY] Tokens available, proceeding normally')
            return
          }
        }
        const url = new URL(window.location.href)
        const hasOAuthParams =
          url.searchParams.has('code') ||
          url.searchParams.has('state') ||
          url.searchParams.has('error') ||
          url.searchParams.has('error_description')

        // No token yet – could be first login with provider linking. Retry once.
        let retryAttempted = '0'
        let lastProvider = ''
        try {
          retryAttempted = sessionStorage.getItem('auth:retryAttempted') || '0'
          lastProvider = sessionStorage.getItem('auth:lastProvider') || ''
        } catch {}
        console.warn('[OAUTH][MISSING_TOKEN] Considering retry', {
          retryAttempted,
          lastProvider,
          hasOAuthParams,
        })

        if (hasOAuthParams && retryAttempted === '0' && lastProvider) {
          try {
            sessionStorage.setItem('auth:retryAttempted', '1')
          } catch {}

          // Small delay to allow AdminLinkProvider to complete
          await new Promise((r) => setTimeout(r, 400))
          console.log(
            '[OAUTH][RETRY] Retrying signInWithRedirect for',
            lastProvider
          )

          if (lastProvider === 'Google') {
            await signInWithRedirect({
              provider: 'Google',
              customState: 'login-retry',
            })
            return
          }
          if (lastProvider === 'Kakao') {
            await signInWithRedirect({
              provider: { custom: 'Kakao' },
              customState: 'login-retry',
            })
            return
          }
          if (lastProvider === 'Microsoft') {
            await signInWithRedirect({
              provider: { custom: 'Microsoft' },
              customState: 'login-retry',
            })
            return
          }
        }
        console.warn('[OAUTH][MISSING_TOKEN] No retry performed')
      } catch (error) {
        // Swallow – user may not be authenticated yet; normal flow continues
        console.warn('[OAUTH][ERROR] ensureSessionOrRetry failed', error)
      }
    }

    // Run on initial mount only (post-redirect load)
    ensureSessionOrRetry()
  }, [setSession, navigate])

  // Show loading while auth is initializing
  useEffect(() => {
    if (!isAuthReady) {
      dispatch(setAuthLoading(true))
    } else {
      dispatch(setAuthLoading(false))
    }
  }, [isAuthReady, dispatch])

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthRoutesConfig />
      </LanguageProvider>
    </ErrorBoundary>
  )
}
